import {invoke, isTauri} from "@tauri-apps/api/core";
import {open as openFileDialog} from "@tauri-apps/plugin-dialog";
import {showErrorNotification} from "@/common/notifications.tsx";


const REQUIRED_NEUROTUNE_TAURI_VERSION = "3.0"
let neurotuneIpcVersion: string;
let neurotuneIpcVersionSupported: boolean;

/**
 * Invokes a Tauri command if running in a Tauri environment.
 * @param command The command name to invoke.
 * @param args Optional arguments for the command.
 * @returns Promise resolving to the command result, or rejects if Tauri is not available.
 */
export async function invokeTauriCommand<T = any>(command: string, args?: Record<string, any>): Promise<T> {
    if (isTauri()) {

        if (command === "get_neurotune_ipc_version" || await isNeurotuneIPCVersionSupported()) {
            return (await invoke(command, args)) as T;
        } else {
            throw new Error(`Neurotune Tauri IPC version ${neurotuneIpcVersion} is not supported. Please update to at least version ${REQUIRED_NEUROTUNE_TAURI_VERSION}.`);
        }
    } else {
        throw new Error('Tauri is not available. This function can only be used within a Tauri application.');
    }
}

export const isNeurotuneIPCVersionSupported = async () => {

    // if we have already checked the version, return the cached result
    if (neurotuneIpcVersionSupported !== undefined) {
        return neurotuneIpcVersionSupported;
    } else {
        // this can execute multiple times in parallel, but that's ok
        console.log("Checking Neurotune Tauri IPC version compatibility...");
        return await invokeTauriCommand("get_neurotune_ipc_version").then((version: string) => {
            neurotuneIpcVersion = version
            const [majorA, minorA, patchA] = version.split('.').map(Number);
            const [requiredMajor, requiredMinor, requiredPatch] = REQUIRED_NEUROTUNE_TAURI_VERSION.split('.').map(Number);
            console.log(`Current Neurotune Tauri IPC version: ${version}, Required version: ${REQUIRED_NEUROTUNE_TAURI_VERSION}`);
            // Check version compatibility using standard semantic versioning rules
            // Major versions must be equal
            // If requiredMinor version is defined then minorA must be >= requiredMinor
            // If requiredPatch version is defined then patchA must be >= requiredPatch
            if (majorA === requiredMajor && (!requiredMinor || minorA >= requiredMinor) && (!requiredPatch || patchA >= requiredPatch)) {
                neurotuneIpcVersionSupported = true;
                return neurotuneIpcVersion
            } else {
                console.warn(`Neurotune Tauri IPC version ${version} is not supported. Please update to at least version ${REQUIRED_NEUROTUNE_TAURI_VERSION}.`);
                neurotuneIpcVersionSupported = false
                showErrorNotification({
                    title: "Please update to a newer version of Neurotune",
                    message: `Neurotune Tauri on ${neurotuneIpcVersion} is not supported. Please update Neurotune.`
                })
                return neurotuneIpcVersionSupported
            }
        }).catch((error) => {
            console.error("Failed to get Neurotune Tauri IPC version:", error);
            neurotuneIpcVersionSupported = false;
            return neurotuneIpcVersionSupported;
        })
    }
}

/**
 * Checks if the app is running in a Tauri environment.
 * @returns True if Tauri is available, false otherwise.
 */
export function isTauriAvailable(): boolean {
    return isTauri();
}

export async function selectDirectoryTauri(): Promise<string | null> {
    return await openFileDialog({
        directory: true,
        multiple: false,
    });
}